import { DataGrid } from "@mui/x-data-grid";
import { getTransactionsByWeek, getFilters, getApprovals, getRnrs, finalizeWeek } from "./api";
import { useEffect, useMemo, useState } from "react";
import TransactionModal from "./TransactionModal";
import { Chip, Button } from "@mui/material";
import { useParams } from "react-router-dom";

export default function ReviewPage() {
  const { id } = useParams();

  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState([]);
  const [approvals, setApprovals] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [rnrs, setRnrs] = useState([]);

  const saveManualSplit = (bankRef, splits) => {
    console.log("Saving manual splits for", bankRef, splits);
    setTransactions(prev =>
      prev.map(t =>
        t.bank_ref === bankRef
          ? { ...t, splits }
          : t
      )
    );
    
    // Märgime tehingu kohe kinnitatuks (approved), kuna see on käsitöö
    setApprovals(prev => ({
      ...prev,
      [bankRef]: true
    }));
  };

  // ---------------------------
  // load transactions
  // ---------------------------

  const fetchData = async () => {
    if (!id) return;

    const tx = await getTransactionsByWeek(id);
    setTransactions(tx);

    const f = await getFilters();
    setFilters(f);

    const a = await getApprovals(id);
    setApprovals(a);

    const r = await getRnrs();
    setRnrs(r);
  }

  useEffect(() => {
    fetchData();
  }, [id]);

  // ---------------------------
  // MATCH ENGINE
  // ---------------------------
  const evaluateRow = (tx) => {
    const hasSplits = tx.splits?.length > 0;

    if (hasSplits) {
      return {
        matchLevel: "YELLOW", // 👈 force yellow
        filterId: null,
        rnr: null,
        approved: true // already handled manually
      };
    }

    const approved = approvals[tx.bank_ref] || false;

    for (let f of filters) {
      const matchDirection = f.direction === tx.direction;
      const matchDescription =
        tx.description.toLowerCase().includes(f.description.toLowerCase());

      if (f.type === "GREEN") {
        const matchParty = tx.name === f.party;
        if (matchDirection && matchDescription && matchParty) {
          return {
            matchLevel: "GREEN",
            filterId: f.id,
            rnr: f.rnr,
            approved: true // greens auto approved
          };
        }
      }

      if (f.type === "YELLOW" && matchDirection && matchDescription) {
        return {
          matchLevel: "YELLOW",
          filterId: f.id,
          rnr: f.rnr,
          approved
        };
      }
    }

    return {
      id: tx.id,
      matchLevel: "NONE",
      filterId: null,
      rnr: null,
      approved: false
    };
  };

  // ---------------------------
  // build rows for grid
  // ---------------------------
  const rows = useMemo(() => {
    return (transactions ?? []).map(tx => ({
    id: tx.id,
    ...tx,
    isSplit: !!tx.splits?.length,
    ...evaluateRow(tx)
  }));
}, [transactions, filters, approvals]);

  // ---------------------------
  // toggle approval
  // ---------------------------
  const toggleApproval = (row) => {
    setApprovals(prev => ({
      ...prev,
      [row.bank_ref]: !prev[row.bank_ref]
    }));
  };

  // ---------------------------
  // create / update filter
  // ---------------------------
  const saveFilter = (row, type, rnr) => {
    if (!row.filterId) {
      const newFilter = {
        id: Date.now().toString(),
        type,
        direction: row.direction,
        description: row.description,
        party: type === "GREEN" ? row.name : null,
        rnr
      };
      setFilters(prev => [...prev, newFilter]);
    } else {
      setFilters(prev =>
        prev.map(f =>
          f.id === row.filterId
            ? {
                ...f,
                type,
                party: type === "GREEN" ? row.name : null,
                rnr
              }
            : f
        )
      );
    }

    if (type === "GREEN") {
      setApprovals(prev => ({ ...prev, [row.bank_ref]: true }));
    } else {
      setApprovals(prev => ({ ...prev, [row.bank_ref]: false }));
    }
  };

  const upgradeToGreen = (row) => {
    setFilters(prev =>
      prev.map(f =>
        f.id === row.filterId
          ? { ...f, type: "GREEN", party: row.name }
          : f
      )
    );
  };

  const downgradeToYellow = (row) => {
    setFilters(prev =>
      prev.map(f =>
        f.id === row.filterId
          ? { ...f, type: "YELLOW", party: null }
          : f
      )
    );
  };

  const removeFilter = (row) => {
    setFilters(prev => prev.filter(f => f.id !== row.filterId));
  };

  // ---------------------------
  // finalize
  // ---------------------------
  const finalize = async () => {
    const pending = rows.filter(
      r => r.matchLevel === "YELLOW" && !r.approved
    );

    if (pending.length > 0) {
      const proceed = window.confirm(
        `${pending.length} suggested rows still need review. Continue anyway?`
      );
      if (!proceed) return;
    }

    const payload = {
      transactions: rows.map(r => ({
        ...r,
        splits: r.splits ?? []
      })),
      filters,
      approvals
    };

    console.log("Sending:", payload);

    if (id) {
      await finalizeWeek(id, payload);
      alert("Document updated!");

      window.location.href = "/accounting/" + id;
    }
  };

  // ---------------------------
  // CHIP RENDERER
  // ---------------------------
  const renderStatusChip = (row) => {
    const isSplit = row.splits?.length > 0;
    
    if (row.matchLevel === "GREEN") {
      return (
        <Chip
          label={row.approved ? "Manual" : "Auto"}
          color="success"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleApproval(row);
          }}
        />
      );
    }

    if (row.matchLevel === "YELLOW") {
      const style = isSplit
        ? {
            backgroundColor: "#bbdefb", // 👈 darker yellow for splits
            fontWeight: 600
          }
        : row.approved
        ? {
            backgroundColor: "#bbdefb"
          }
        : {
            backgroundColor: "#fff3b0"
          };

      const label = isSplit
        ? "Split"
        : row.approved
        ? "Reviewed"
        : "Suggested";

      return (
        <Chip
          label={label}
          sx={style}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleApproval(row);
          }}
        />
      );
    }

    return null;
  };

  // ---------------------------
  // columns
  // ---------------------------
  const columns = [
    { field: "bank_ref", headerName: "Bank Ref", width: 180 },

    { field: "name", headerName: "Name", width: 180 },

    { field: "iban", headerName: "IBAN", width: 180 },

    { field: "description", headerName: "Description", width: 220 },

    {
      field: "direction",
      headerName: "Direction",
      width: 110,
      renderCell: p => (
        <div>{p.value === "DBIT" ? "Debit" : "Credit"}</div>
      )
    },

    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      renderCell: p => (
        <div>
          {p.row.currency} {p.value?.toFixed(2)}
        </div>
      )
    },

    { field: "date", headerName: "Date", width: 140 },

    { field: "rnr",
      headerName: "RNR",
      width: 120,
      renderCell: (p) => {
        if (p.row.splits?.length) {
          return p.row.splits.map(s => s.rnr).join(", ");
        }

        return p.row.rnr;
      }
    },

    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (p) => renderStatusChip(p.row)
    }
  ];


  return (
    <div>
      
      {rows.length > 0 ? (
      <>
        <h2>Review Bank Transactions</h2>

        {selectedRow && (
          <TransactionModal
            row={selectedRow}
            rnrs={rnrs}
            onClose={() => setSelectedRow(null)}
            onConfirm={(row, type, rnr) => {
              saveFilter(row, type, rnr);
              setSelectedRow(null);
            }}
            onUpgrade={upgradeToGreen}
            onDowngrade={downgradeToYellow}
            onRemove={removeFilter}
            onSaveSplit={(bankRef, splits) => {
              saveManualSplit(bankRef, splits);
              setSelectedRow(null);
            }}
          />
        )}

        <DataGrid
          disableRowSelectionOnClick
          rows={rows}
          columns={columns}
          getRowId={r => r.id}
          onRowClick={(p) => setSelectedRow(p.row)}
          getRowClassName={(params) => {
            if (params.row.direction === "DBIT") return "row-debit";
            if (params.row.direction === "CRDT") return "row-credit";
            return "";
          }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fff",
            },
          }}
        />

        <Button variant="contained" onClick={finalize}>
          Finalize
        </Button>
      </>
      ) : (
        <p>No transactions loaded. Please upload an XML file.</p>
      )}
    </div>
  );
}
